import { FilterInput, SlideFilter } from '@apeiron/common'
import { OrbitalTrackFilterInput } from '@apeiron/orbital'
import { StarTier } from '@apeiron/star'
import { Box, styled, Typography } from '@mui/material'
import {
  ChipIcon,
  ChipLabel,
  ChipToggleButton,
  ChipToggleButtonGroup
} from '@src/components/share/filter/ChipToggle'
import {
  FilterSection,
  FilterSectionContent,
  FilterSectionHeader,
  FilterSectionTitle
} from '@src/components/share/filter/FilterSection'
import RangeSlider from '@src/components/share/filter/RangeSlider'
import ResetFilter from '@src/components/share/filter/ResetFilter'
import useFetchOrbitalTrackFilterJson from '@src/hooks/json/useFetchOrbitalTrackFilterJson'
import * as R from 'ramda'
import { FC, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

const Content = styled(FilterSectionContent)`
  &.MuiAccordionDetails-root {
    display: flex;
    flex-direction: column;
    padding-top: 10px;
  }
`

const ClipSection = styled(Box)`
  border-bottom: 2px solid #151615;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0px 10px 20px 22px;
`

const ClipTitle = styled(Typography)`
  color: #333333;
  font-size: 16px;
  font-weight: 300;
  line-height: 16px;
`

const Form = styled('form')`
  display: flex;
  flex-direction: column;
`

const StyledRangeSlider = styled(RangeSlider)`
  padding: 20px 10px 20px 22px;
`

const TypeAndEffectFilter: FC<Props> = props => {
  const { className, filterInput, onFilterChange } = props

  const { t } = useTranslation()

  const { data: filterData } = useFetchOrbitalTrackFilterJson()

  const tiers = useMemo(() => filterInput.tier ?? [], [filterInput.tier])

  const numberOfFilters = useMemo(() => {
    const countFilters = (
      key: string,
      filter: OrbitalTrackFilterInput
    ): number => {
      const doCountFilters = R.pipe<any, any[], number>(
        R.propOr([], key),
        R.length
      )

      return doCountFilters(filter)
    }

    const numberOfTierFilters = countFilters('tier', filterInput)

    const numberOfCountFilters = R.isNil(filterInput.totalTrackCountMax) ? 0 : 1

    const numberOfAgeFilters = R.isNil(filterInput.agingBuffMax) ? 0 : 1

    return R.sum([
      numberOfTierFilters,
      numberOfCountFilters,
      numberOfAgeFilters
    ])
  }, [filterInput])

  const handleOnReset = useCallback(() => {
    onFilterChange(
      R.omit(
        [
          'agingBuffMax',
          'agingBuffMin',
          'tier',
          'totalTrackCountMax',
          'totalTrackCountMin'
        ],
        filterInput
      )
    )
  }, [filterInput, onFilterChange])

  const handleOnTiersChange = useCallback(
    (_: any, input: number[]) => {
      onFilterChange(R.assoc('tier', input, filterInput))
    },
    [filterInput, onFilterChange]
  )

  const handleOnValueChange = useCallback(
    (filter: FilterInput) => {
      onFilterChange(filter as OrbitalTrackFilterInput)
    },
    [onFilterChange]
  )

  return (
    <FilterSection className={className} defaultExpanded>
      <FilterSectionHeader>
        <FilterSectionTitle>{t('orbit.list.filter.effect')}</FilterSectionTitle>
        <ResetFilter filterCount={numberOfFilters} onReset={handleOnReset} />
      </FilterSectionHeader>
      <Content>
        {!R.isNil(filterData) && (
          <>
            <ClipSection>
              <ClipTitle>{filterData.orbitFilterTier.name}</ClipTitle>
              <ChipToggleButtonGroup
                value={tiers}
                onChange={handleOnTiersChange}
              >
                {R.map(
                  (tier: StarTier) => (
                    <ChipToggleButton key={tier.level} value={tier.level}>
                      <ChipIcon src={tier.image} />
                      <ChipLabel>{tier.type}</ChipLabel>
                    </ChipToggleButton>
                  ),
                  filterData.orbitFilterTier.values || []
                )}
              </ChipToggleButtonGroup>
            </ClipSection>
            <Form>
              {R.map((filter: SlideFilter) => {
                return (
                  <StyledRangeSlider
                    decimalScale={filter.decimalScale}
                    filterOptions={filterInput}
                    key={filter.name}
                    labelIconURL={filter.image}
                    labelText={filter.name}
                    marks={false}
                    max={filter.max.slot}
                    maxValueKey={filter.max.field}
                    min={filter.min.slot}
                    minValueKey={filter.min.field}
                    remark={filter.remark}
                    step={filter.step}
                    onFilterOptionsChange={handleOnValueChange}
                  />
                )
              }, filterData.orbitFilterEffect)}
            </Form>
          </>
        )}
      </Content>
    </FilterSection>
  )
}

type Props = {
  className?: string
  filterInput: OrbitalTrackFilterInput
  onFilterChange: (input: OrbitalTrackFilterInput) => void
}

export default TypeAndEffectFilter
